import React, {
  FC,
  useCallback,
  useState,
  CSSProperties,
  useRef,
  useEffect,
  ReactNode,
} from 'react';
import { UserAgentProvider, UserAgent } from '@quentin-sommer/react-useragent';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import useMounted from 'src/common/hooks/useMounted';
import getClassNames from 'src/common/utils/getClassNames';
import ScrollLock from 'src/common/components/ScrollLock';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import useTimeout from 'src/common/hooks/useTimeout';
import { useTranslation } from 'next-i18next';
import styles from './BottomDrawer.module.scss';

export type BottomDrawerProps = {
  /**
   * The content that will be displayed inside the BottomSheet.
   * Closed view: auto height
   * Open view: if with overflow scrolling, ensure it has 100% height
   */
  children: React.ReactNode;
  /**
   * Function that is called when the BottomSheet is closed.
   */
  onClose?: () => void;

  setOpen: (value: boolean) => void;
  /**
   * Show a small animation the first time the drawer is shown so users
   * know they can interact with it.
   */
  highlightReveal?: boolean;
  /**
   * When true, the drawer remains mounted but is visually hidden.
   * Use this to hide the drawer while a gallery view is active so that
   * the gallery can take over the screen without the drawer overlapping it,
   * while still preserving the drawer's internal state and layout.
   */
  hidden?: boolean;
  /**
   * State and Set State Action for controlling when sheet is open.
   * This is handled by parent for passing to children when needed to reset ScrollLock
   * in order to change scroll position to top when navigating to new route
   */
  isOpen: boolean;
  /**
   * Separated from the body, not included in height calculations but still positioned
   * relative to drawer position. It is wrapped in an absolute div, set to the drawer position.
   * It will be placed above other content
   */
  outside?: ReactNode;
  /**
   * No rounded corners
   */
  hardEdge?: boolean;
  /**
   * Drawer vertical scrolling. Defaults to 'auto'.
   * Set to hidden if scrolling is managed in the components
   */
  overflow?: 'hidden' | 'auto';
  disabled?: boolean;
  /**
   * Drawer height when closed. Defaults to 100.
   */
  drawerHeightWhenClosed?: number;
};

const isLeftMouseButton = (e: React.PointerEvent<HTMLButtonElement> | MouseEvent): boolean => {
  if ('buttons' in e) {
    return e.buttons === 1;
  }
  return false;
};

const HIGHLIGHT_TIME_MS = 1200;
const MIN_DRAG_DISTANCE = 5; // in pixels
const MAX_OVERLAY_OPACITY = 0.6; // css opacity
const OPEN_SNAP_GAP = 16; // px

// includes delay and safety padding
export const SHOW_HIGHLIGHT_TIME_MS = HIGHLIGHT_TIME_MS + HIGHLIGHT_TIME_MS / 4 + 100;

const BottomDrawer: FC<BottomDrawerProps> = ({
  children,
  isOpen,
  setOpen,
  onClose,
  highlightReveal,
  hidden,
  outside,
  hardEdge,
  overflow = 'auto',
  disabled,
  drawerHeightWhenClosed = 100,
}) => {
  const [t] = useTranslation('common');
  const isMounted = useMounted();
  const [windowHeight, setWindowHeight] = useState(1);
  const [showOverlay, setShowOverlay] = useState(isOpen);
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [closedContentHeight, setClosedContentHeight] = useState(0);
  // Only for closing the drawer when resized

  // allow animating from off-screen on load
  const [animate, setAnimate] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  // Snap point as a percentage number (0..100)
  // Snap points are from "top of screen" ie. 0% is full screen, but the drawer is shown
  // from the bottom of the screen which is why it's inverted
  const closedSnapPoint = (1 - closedContentHeight / windowHeight) * 100;
  const openSnapPoint = (OPEN_SNAP_GAP / windowHeight) * 100;

  // Percent (0..100) of screen the sheet takes up, inverted from snap points
  // i.e. calculated from top of screen.
  // This just keeps the math simpler when following the pointer
  const [sheetPosition, setSheetPosition] = useState(closedSnapPoint);
  const [hasHighlighted, setHasHighlighted] = useState(false);
  const [highlightInteract, setHighlightInteract] = useState(false);

  useTimeout(() => {
    setHasHighlighted(true);
  }, HIGHLIGHT_TIME_MS);

  const open = useCallback(() => {
    // locks body scroll
    setOpen(true);
    setAnimate(true);
    setSheetPosition(openSnapPoint);
  }, [openSnapPoint, setOpen]);

  const close = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    // unlocks body scroll
    setOpen(false);
    setAnimate(true);
    onClose?.();
    setSheetPosition(closedSnapPoint);
  }, [closedSnapPoint, onClose, setOpen]);

  const toggle = useCallback(() => {
    if (isDragging || disabled) return;
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isDragging, disabled, isOpen, close, open]);

  const drag = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!isLeftMouseButton(e) || disabled) return;
      const sheetStart = (isOpen ? openSnapPoint : closedSnapPoint) / 100;
      const startY = e.clientY;
      const offset = startY / windowHeight - sheetStart;

      let dragDirection: string | null = null;
      let lastY = startY;

      // TODO: dispose on unmount
      const onDragMove = (moveEvent: PointerEvent) => {
        setIsDragging(true);
        const currentY = moveEvent.clientY;
        let position = currentY / windowHeight - offset;

        // We don't want the sheet's height to go beyond or below the snap points.
        // Invert the snap points since position is from top, snaps are from bottom
        position = Math.min(closedSnapPoint, Math.max(openSnapPoint, position * 100));

        setSheetPosition(position);
        setAnimate(false);
        setShowOverlay(true);
        if (currentY < lastY) {
          dragDirection = 'up';
        } else if (currentY > lastY) {
          dragDirection = 'down';
        }

        // Show main content if dragging
        if (Math.abs(startY - currentY) >= MIN_DRAG_DISTANCE) {
          setOpen(true);
        }
        lastY = currentY;
      };

      // TODO: dispose on unmount
      const onDragEnd = () => {
        document.removeEventListener('pointermove', onDragMove);
        document.removeEventListener('pointerup', onDragEnd);

        // Don't apply drag effect if movement is small. Click will trigger
        if (Math.abs(startY - lastY) < MIN_DRAG_DISTANCE) {
          setIsDragging(false);
          return;
        }
        // Delayed dragend cancellation to prevent overlap with click
        requestAnimationFrame(() => {
          setIsDragging(false);
        });

        const shouldSnapToOpen = dragDirection === 'up';
        const shouldSnapToClosed = dragDirection === 'down';

        if (shouldSnapToOpen) {
          open();
        }

        if (shouldSnapToClosed) {
          close();
        }
        setHighlightInteract(false);
      };
      document.addEventListener('pointermove', onDragMove);
      document.addEventListener('pointerup', onDragEnd);
      setHighlightInteract(true);
    },
    [
      disabled,
      isOpen,
      openSnapPoint,
      closedSnapPoint,
      windowHeight,
      setOpen,
      open,
      close,
    ],
  );

  const dragArea = Math.abs(openSnapPoint - closedSnapPoint);
  const activeSnapPoint = isOpen ? openSnapPoint : closedSnapPoint;
  const position = isDragging ? sheetPosition : activeSnapPoint;
  // Re-align position based on openSnapPoint
  const positionPercent = (position - openSnapPoint) / dragArea;
  const overlayOpacity = (1 - positionPercent) * MAX_OVERLAY_OPACITY;

  const enableAnimateAfterRender = useAfterRenderCallback(() => {
    setAnimate(true);
  });

  const resizeContent = useCallback(() => {
    if (!bodyRef.current || isOpen) return;

    setClosedContentHeight(
      bodyRef.current.offsetHeight + bodyRef.current.offsetTop,
    );
  }, [isOpen]);

  useEffect(() => {
    resizeContent();
  }, [isMounted, resizeContent]);

  useEffect(() => {
    const onResize = () => {
      setAnimate(false);
      if (windowHeight !== window.innerHeight) {
        setWindowHeight(window.innerHeight);
      } else {
        setSheetPosition(activeSnapPoint);
        resizeContent();
      }
      enableAnimateAfterRender();
    };

    setWindowHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [activeSnapPoint, closedContentHeight, windowHeight, resizeContent, enableAnimateAfterRender]);

  useEffect(() => {
    // isOpen can be changed externally
    setHighlightInteract(true);
    setShowOverlay(isOpen);
  }, [isOpen]);

  const highlightAnimationComplete = useCallback(() => {
    setHighlightInteract(false);
  }, []);

  // Use pixel values for layout to ensure visual stability
  const drawerPositionOffset = isOpen ? -OPEN_SNAP_GAP : 0;
  const positionPx = Math.round((position / 100) * windowHeight) + drawerPositionOffset;
  const height = !isOpen ? drawerHeightWhenClosed : 100 - openSnapPoint;
  const heightPx = Math.round((height / 100) * windowHeight)
    // Avoid oddities if window height is smaller than drawer height when closed
    + (positionPx < 0 ? -positionPx : 0);

  return isMounted ? ReactDOM.createPortal(
    <>
      {isOpen && (
        <UserAgentProvider ua={window?.navigator?.userAgent}>
          <UserAgent mobile>
            {/**
              * On mobile devices, setting the body to position: fixed causes the page to jump to
              * the top.
              * Simply using overflow: hidden is not effective, as it doesn't prevent scrolling.
              * To avoid this issue, we apply additional calculations to the body to maintain its
              * scroll position while in position: fixed.
              */}
            {(uaIsMobile: boolean) => (
              <ScrollLock calculateScrollOffset={uaIsMobile} />
            )}
          </UserAgent>
        </UserAgentProvider>
      )}
      <div
        className={getClassNames(styles, 'root', classNames({ hidden }))}
        ref={rootRef}
      >
        <button
          type="button"
          className={getClassNames(styles, 'overlay', classNames({ hide: !showOverlay, animate }))}
          style={{ opacity: overlayOpacity.toFixed(2) }}
          onClick={close}
          aria-label={t('bottom-drawer--close')}
        />
        <div
          className={getClassNames(styles, 'content', classNames({ animate }))}
          style={{
            transform: `translate3d(0, ${positionPx}px, 0)`,
            '--drawer-height': `${heightPx}px`,
          } as CSSProperties}
        >
          <div
            className={styles.outside}
          >
            {outside}
          </div>
          {!disabled && (
            <button
              className={styles.dragButton}
              onPointerDown={drag}
              onClick={toggle}
              type="button"
              aria-label={t(`bottom-drawer--${isOpen ? 'close' : 'open'}`)}
              data-test="open-bottom-sheet-toggle-button"
            >
              <span
                className={getClassNames(styles, 'dragBar', classNames({ highlightFirstTime: highlightReveal && !hasHighlighted, highlightInteract }))}
                style={{ '--highlight-time': `${HIGHLIGHT_TIME_MS / 1000}s` } as CSSProperties}
                onAnimationEnd={highlightAnimationComplete}
              />
            </button>
          )}
          <div className={getClassNames(styles, 'blocker', classNames({ isBlocked: isOpen }))} />
          <div className={getClassNames(styles, 'body', classNames(`overflow_${overflow}`, { isOpen, hardEdge }))} ref={bodyRef}>
            {children}
          </div>
        </div>
      </div>
    </>,
    document.body,
  ) : null;
};

export default BottomDrawer;
